import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Modal, Segmented } from '@koyomi/ui';
import { ApiError } from '../../lib/api.ts';
import { useBusiness } from '../../lib/queries.ts';
import { fmtDateTime } from '../../lib/time.ts';

export interface AffectedDto { id: string; startsAt: string; endsAt: string; clientName: string; services: string[]; memberName: string; reasons: string[]; substituteMemberId: string | null; substituteName: string | null }
type Decision = 'SUBSTITUTE' | 'RESCHEDULE_VIA_AI' | 'KEEP_AS_EXCEPTION';
export interface ResolutionDto { appointmentId: string; resolution: Decision; substituteMemberId?: string | null }

interface Pending { label: string; affected: AffectedDto[]; run: (resolutions: ResolutionDto[]) => Promise<unknown>; resolve: (v: unknown) => void; reject: (e: unknown) => void }
const Ctx = createContext<((fn: (resolutions?: ResolutionDto[]) => Promise<unknown>) => Promise<unknown>) | null>(null);

/**
 * Configuração nunca invalida agenda em silêncio: qualquer chamada que devolva 409 CONFIG_CONFLICT
 * abre o diálogo de decisão por agendamento e reenvia com as resoluções (mockup 20).
 */
export function ConflictProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const withConflict = useCallback(async (fn: (resolutions?: ResolutionDto[]) => Promise<unknown>) => {
    try {
      return await fn();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONFIG_CONFLICT' && (err.details as { affectedAppointments?: AffectedDto[] })?.affectedAppointments) {
        const details = err.details as { label: string; affectedAppointments: AffectedDto[] };
        return new Promise((resolve, reject) => setPending({ label: details.label, affected: details.affectedAppointments, run: (r) => fn(r), resolve, reject }));
      }
      throw err;
    }
  }, []);
  return (
    <Ctx.Provider value={withConflict}>
      {children}
      {pending && <ConflictDialog pending={pending} onClose={() => { pending.reject(new Error('cancelled')); setPending(null); }} onDone={(v) => { pending.resolve(v); setPending(null); }} />}
    </Ctx.Provider>
  );
}

export function useWithConflict() {
  const fn = useContext(Ctx);
  if (!fn) throw new Error('ConflictProvider ausente');
  return fn;
}

function ConflictDialog({ pending, onClose, onDone }: { pending: Pending; onClose: () => void; onDone: (v: unknown) => void }) {
  const { t, i18n } = useTranslation(['calendar', 'common']);
  const business = useBusiness();
  const tz = business.data?.timezone ?? 'UTC';
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const decided = Object.keys(decisions).length;
  const total = pending.affected.length;
  const opts = useMemo(() => (['SUBSTITUTE', 'RESCHEDULE_VIA_AI', 'KEEP_AS_EXCEPTION'] as const), []);
  const applyAll = (d: Decision) => setDecisions(Object.fromEntries(pending.affected.filter((a) => d !== 'SUBSTITUTE' || a.substituteMemberId).map((a) => [a.id, d])));
  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const v = await pending.run(pending.affected.map((a) => ({ appointmentId: a.id, resolution: decisions[a.id]!, substituteMemberId: a.substituteMemberId })));
      onDone(v);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common:errors.INTERNAL'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} size="lg" footer={<><span className="k-muted" style={{ marginRight: 'auto' }}>{t('calendar:conflict.decided', { decided, total, pending: total - decided })}</span><Button onClick={onClose}>{t('common:actions.cancel')}</Button><Button variant="primary" disabled={decided < total} loading={busy} onClick={apply}>{t('calendar:conflict.apply')}</Button></>}>
      <div className="k-stack">
        <div className="k-row"><Chip tone="warn">{t('calendar:conflict.badge', { count: total })}</Chip><span className="k-muted">{t('calendar:conflict.nothingYet')}</span></div>
        <h3 className="k-panel-title">{t(`calendar:conflict.labels.${pending.label}`, { defaultValue: pending.label })}</h3>
        <p className="k-muted" style={{ margin: 0 }}>{t('calendar:conflict.hint')}</p>
        <div className="k-row" style={{ flexWrap: 'wrap' }}>
          <span className="k-muted">{t('calendar:conflict.applyAll')}</span>
          <Button size="sm" onClick={() => applyAll('SUBSTITUTE')}>{t('calendar:conflict.substituteWhenPossible')}</Button>
          <Button size="sm" onClick={() => applyAll('RESCHEDULE_VIA_AI')}>{t('calendar:conflict.RESCHEDULE_VIA_AI')}</Button>
          <Button size="sm" onClick={() => applyAll('KEEP_AS_EXCEPTION')}>{t('calendar:conflict.KEEP_AS_EXCEPTION')}</Button>
        </div>
        <div className="k-table__wrap">
          <table className="k-table">
            <thead><tr><th>{t('calendar:conflict.cols.when')}</th><th>{t('calendar:conflict.cols.client')}</th><th>{t('calendar:conflict.cols.service')}</th><th>{t('calendar:conflict.cols.decision')}</th></tr></thead>
            <tbody>
              {pending.affected.map((a) => (
                <tr key={a.id} style={{ cursor: 'default' }}>
                  <td data-label={t('calendar:conflict.cols.when')}><strong>{fmtDateTime(a.startsAt, tz, i18n.language, { weekday: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</strong></td>
                  <td data-label={t('calendar:conflict.cols.client')}>{a.clientName}</td>
                  <td data-label={t('calendar:conflict.cols.service')}>{a.services.join(' + ')}</td>
                  <td data-label={t('calendar:conflict.cols.decision')}>
                    <Segmented value={decisions[a.id] ?? ('' as Decision)} onChange={(d) => setDecisions({ ...decisions, [a.id]: d })} options={opts.filter((o) => o !== 'SUBSTITUTE' || a.substituteMemberId).map((o) => ({ value: o, label: o === 'SUBSTITUTE' ? `${t('calendar:conflict.SUBSTITUTE')} · ${a.substituteName}` : t(`calendar:conflict.${o}`) }))} />
                    {!a.substituteMemberId && <div className="k-muted" style={{ fontSize: 12, marginTop: 4 }}>{t('calendar:conflict.noSubstitute')}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {error && <div className="k-field__error">{error}</div>}
      </div>
    </Modal>
  );
}
